import React, { useEffect, useMemo, useState } from "react";
import { cachedApiRequest } from "../../lib/api";
import "./ManagedFaqSection.css";

const EMPTY_FAQS = [];

export const pageFaqDefaults = {
  home: [
    ["What can I do with Mailsnappy?", "You can create email campaigns, use responsive templates, manage subscribers, build automation, segment audiences, and review campaign analytics from one dashboard."],
    ["Do I need coding experience to create emails?", "No. Mailsnappy is built for teams that want to design professional emails with templates, visual editing, and AI-assisted creation without writing code."],
    ["Can Mailsnappy help with email automation?", "Yes. You can build automated welcome, follow-up, and customer journey emails so subscribers receive timely messages with less manual work."],
  ],
  features: [
    ["Which email marketing features are included?", "Mailsnappy includes campaign creation, templates, signup forms, subscriber management, segmentation, automation, analytics, and sender tools."],
    ["Can I manage audiences and campaigns together?", "Yes. Audience tools, contact fields, tags, segments, templates, campaigns, and reports are connected so your email workflow stays organized."],
    ["Does Mailsnappy support campaign reporting?", "Yes. Campaign analytics help you track opens, clicks, engagement, subscriber activity, and performance trends for future improvement."],
  ],
  templates: [
    ["Are Mailsnappy templates responsive?", "Yes. Templates are designed to work across desktop and mobile inboxes so your campaigns look polished on different devices."],
    ["Can I customize templates for my brand?", "Yes. You can update copy, colors, images, links, and layout sections before using a template in a campaign."],
    ["Can I use templates from the dashboard?", "Yes. Browse public templates on the website and open them in the dashboard editor to start designing your campaign."],
  ],
  "ai-template-designer": [
    ["How does the AI template designer work?", "Describe the email campaign you want, including your offer, audience, tone, and style, and the AI designer generates responsive HTML email content."],
    ["Can I copy or download the generated HTML?", "Yes. After generation, you can preview the email, copy the HTML, or download it for later editing and use."],
    ["What should I include in my prompt?", "Include the campaign goal, brand style, audience, offer details, preferred colors, and any image URL you want included."],
  ],
  pricing: [
    ["Is there a free Mailsnappy plan?", "Yes. The free plan lets you start creating campaigns and try core email marketing tools without a credit card."],
    ["How does paid pricing scale?", "Paid pricing scales by monthly email volume, so you can choose the plan that matches your current campaign needs."],
    ["Can nonprofits get a discount?", "Yes. Eligible nonprofit and charity organizations can receive a lifetime discount on paid plans."],
  ],
  automation: [
    ["What can I automate in Mailsnappy?", "You can create welcome sequences, follow-up messages, nurturing campaigns, and subscriber journeys that run automatically."],
    ["Can automation work with segmentation?", "Yes. Automation works best with subscriber data, tags, and segments so the right people receive the right messages."],
    ["Do automated emails include reporting?", "Yes. You can review performance to understand engagement and improve future automated campaigns."],
  ],
  analytics: [
    ["Which email analytics are available?", "Mailsnappy focuses on practical campaign metrics such as opens, clicks, engagement, subscriber activity, and performance trends."],
    ["Can analytics improve future campaigns?", "Yes. Reviewing campaign reports helps you understand what subscribers engage with and adjust content, timing, and targeting."],
    ["Are reports useful for automated campaigns?", "Yes. Analytics help you evaluate both regular campaigns and automated email journeys."],
  ],
  guide: [
    ["What does the email marketing guide cover?", "The guide covers getting started, subject lines, effective emails, personalization, scheduling, and measuring campaign success."],
    ["Is the guide useful for beginners?", "Yes. It is written for people who want practical email marketing steps without needing advanced technical knowledge."],
    ["Can I use guide advice inside Mailsnappy?", "Yes. The guide topics connect directly to creating templates, building campaigns, segmenting subscribers, and reviewing reports."],
  ],
  alternatives: [
    ["What are Mailsnappy alternative pages for?", "They help compare Mailsnappy with other email marketing platforms using workflow, fit, and feature focus."],
    ["Are competitor comparisons meant to replace full product research?", "No. They provide practical starting points, and you should still review each provider based on your business needs."],
    ["Who should consider Mailsnappy?", "Teams that want focused email campaigns, templates, automation, segmentation, and analytics in one workspace may find Mailsnappy a good fit."],
  ],
};

export function normalizeFaqItems(items = EMPTY_FAQS) {
  return (Array.isArray(items) ? items : [])
    .map((item, index) => {
      if (Array.isArray(item)) {
        return {
          id: `fallback-${index}`,
          question: item[0],
          answer: item[1],
          sort_order: index,
        };
      }

      return {
        id: item?.id ?? `faq-${index}`,
        question: item?.question,
        answer: item?.answer,
        sort_order: item?.sort_order ?? index,
      };
    })
    .filter((item) => item.question && item.answer);
}

export function useManagedFaqs(pageKey, fallbackItems = EMPTY_FAQS) {
  const fallback = useMemo(() => normalizeFaqItems(fallbackItems), [fallbackItems]);
  const [items, setItems] = useState(fallback);

  useEffect(() => {
    let cancelled = false;
    setItems(fallback);

    if (!pageKey) return () => {
      cancelled = true;
    };

    cachedApiRequest(`/public/faqs/${encodeURIComponent(pageKey)}`, {
      cacheKey: `public-faqs-${pageKey}`,
      cacheTtlMs: 60 * 1000,
    })
      .then((data) => {
        if (cancelled) return;
        const rows = normalizeFaqItems(data?.data);
        setItems(rows.length || data?.has_records ? rows : fallback);
      })
      .catch(() => {
        if (!cancelled) setItems(fallback);
      });

    return () => {
      cancelled = true;
    };
  }, [pageKey, fallback]);

  return items;
}

function useFaqSchema(pageKey, items) {
  useEffect(() => {
    if (typeof document === "undefined" || !pageKey || !items.length) return undefined;

    const element = document.createElement("script");
    element.type = "application/ld+json";
    element.dataset.managedFaqSchema = pageKey;
    element.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: items.map((item) => ({
        "@type": "Question",
        name: item.question,
        acceptedAnswer: {
          "@type": "Answer",
          text: item.answer,
        },
      })),
    });
    document.head.appendChild(element);

    return () => element.remove();
  }, [pageKey, items]);
}

export default function ManagedFaqSection({
  pageKey,
  heading = "Frequently asked questions",
  eyebrow = "FAQs",
  intro,
  fallback = EMPTY_FAQS,
  className = "",
  variant = "section",
}) {
  const items = useManagedFaqs(pageKey, fallback);
  useFaqSchema(pageKey, items);

  if (!items.length) return null;

  if (variant === "inline") {
    return (
      <div className={`managed-faq-inline ${className}`.trim()}>
        <span>{eyebrow}</span>
        <h2>{heading}</h2>
        {intro && <p>{intro}</p>}
        {items.map((item) => (
          <article key={`${item.id}-${item.question}`}>
            <h3>{item.question}</h3>
            <p>{item.answer}</p>
          </article>
        ))}
      </div>
    );
  }

  return (
    <section className={`managed-faq-section ${className}`.trim()} aria-label={heading}>
      <div className="managed-faq-heading">
        <span>{eyebrow}</span>
        <h2>{heading}</h2>
        {intro && <p>{intro}</p>}
      </div>
      <div className="managed-faq-list">
        {items.map((item, index) => (
          <details className="managed-faq-item" key={`${item.id}-${item.question}`} open={index === 0}>
            <summary>
              <span>{item.question}</span>
              <b aria-hidden="true">+</b>
            </summary>
            <p>{item.answer}</p>
          </details>
        ))}
      </div>
    </section>
  );
}